import { startTransition, useRef } from "react"
import type { ComponentProps, FC, ReactElement, RefObject } from "react"

import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"
import { Trans } from "react-i18next"
import { useBoolean, useCss, useIntersection, useUpdate, useUpdateEffect } from "react-use"

import {
    Button,
    CareMailBox,
    EButtonSize,
    ETypographySize,
    ETypographyWeight,
    EWaveLoaderVariant,
    ErrorHint,
    Modal,
    Typography,
    WaveLoader
} from "@/3514/components"
import { withDynamicHeightGuard } from "@/3514/guards"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { EModalId, type IModal, useModalSelector } from "@/3514/store/modal"
import { THEME } from "@/3514/theme"
import { buildCssClassName, getCssIdentifierFromFileName, renderRichContent, withPressHandler } from "@/3514/utils"
import {
    type IUseTranslation,
    type TGetValueFromTranslatedObjectFunction,
    useGetValueFromTranslatedObject,
    useTranslation
} from "@/hooks"
import type { TEmptyCallback } from "@/shared/types/functions"

import type {
    TCoachingChatCreateDisclaimerAwarenessMutation as TMutation,
    TCoachingChatDisclaimerQuery as TQuery
} from "../../api"
import { coachingChatOwlComponents as owls } from "../../components/coaching-chat.owls-components"
import { coachingChatUiConfig as uiConfig } from "../../config"
import { useCoachingChatContext as useContext } from "../../context"
import { coachingChatStyles as chatStyles } from "../../styles"
import type { TCoachingChatGuideModalProps as TModalProps } from "../../types"
import { coachingChatUiUtils as uiUtils } from "../../utils"

const bodyDisplayName: string = "CoachingChatGuideModalBody"
const rendererCssIdentifier: string = getCssIdentifierFromFileName(bodyDisplayName, "lr")
const loaderCssIdentifier: string = getCssIdentifierFromFileName(bodyDisplayName, "lr")

const {
    insertionClassesUtils: { getClassesForLegaliseRecord: getLegaliseClasses }
} = uiUtils
const { chatGuideLegaliseRendererAllowedTags: legaliseAllowedTags, chatGuideModalListConfig: staticList } = uiConfig

const {
    modals: { guide: classes }
} = chatStyles

const Body: FC = (): ReactElement => {
    const { props: modalProps }: IModal<TModalProps> = useModalSelector<TModalProps>(
        EModalId.Participant_CoachingChatGuide
    )

    const mutation: TMutation = modalProps.mutationHook()

    const { t }: IUseTranslation = useTranslation()

    const getValueFromTranslatedObject: TGetValueFromTranslatedObjectFunction = useGetValueFromTranslatedObject()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const {
        guide: { queryHook: useQuery, handleModalClose: onClose }
    }: ReturnType<typeof useContext> = useContext()

    const triggerUpdate: TEmptyCallback = useUpdate()

    const query: TQuery = useQuery()

    const bannerTextCss: string = useCss({
        p: {
            fontSize: `${THEME.FONT_SIZES[12]}px`,
            lineHeight: `${THEME.LINE_HEIGHTS[18]}px`,
            fontWeight: THEME.FONT_WEIGHTS[300],
            color: THEME.COLORS.gray[100],
            marginBottom: 0
        },
        "p > strong": { fontWeight: THEME.FONT_WEIGHTS[800] }
    })

    const sentinelRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const [wasSentinelSeen, setWasSentinelSeen]: ReturnType<typeof useBoolean> = useBoolean(false)

    const intersection: IntersectionObserverEntry = useIntersection(sentinelRef, {
        root: null,
        rootMargin: "0px 0px 0px 0px",
        threshold: 1
    })

    useUpdateEffect((): void => (query.isFetched && triggerUpdate(), void 0), [query.isFetched])

    useUpdateEffect((): void => {
        if (intersection?.isIntersecting) {
            setWasSentinelSeen(true)
            sentinelRef.current?.remove()
        }
    }, [intersection?.isIntersecting])

    function handleModalClose(): void {
        onClose()
        mutation.reset()
    }

    async function submitAwareness(): Promise<void> {
        await mutation.mutateAsync(void 0, {
            onSuccess: (): void => (handleModalClose(), modalProps.onSubmit(), void 0)
        })
    }

    const handleAction: ReturnType<typeof withPressHandler> = withPressHandler((): void =>
        startTransition((): void => {
            if (query.isError || isEmpty(query.data)) return void handleModalClose()

            return void submitAwareness()
        })
    )

    function renderHeading(): ReactElement {
        return (
            <div className={classes.modalBodyHeading}>
                <owls.GuideImage />

                <div className={buildCssClassName(classes.modalBodyHeadingCaption, bannerTextCss)}>
                    <Trans
                        parent="p"
                        i18nKey="entities.coachingChat.guideModal.banner"
                        components={{ strong: <strong /> }}
                    />
                </div>
            </div>
        )
    }

    function renderStaticList(): ReactElement {
        return (
            <ol className={classes.modalBodyTextList}>
                {map(
                    staticList,
                    ({ title, content }, idx: number): ReactElement => (
                        <li key={`item-${idx ** 2}`}>
                            <Typography
                                size={breakpoints.xs || breakpoints.sm ? ETypographySize.Small : ETypographySize.Medium}
                                text={t(title)}
                                weight={ETypographyWeight.Bold}
                                className={classes.modalBodyTextListItemTitle}
                            />
                            <Typography text={t(content)} size={ETypographySize.Small} />
                        </li>
                    )
                )}
            </ol>
        )
    }

    function renderDisclaimer(): ReactElement {
        return query.isFetching ? (
            <WaveLoader
                withRelativePosition
                cssIdentifier={loaderCssIdentifier}
                variant={EWaveLoaderVariant.DarkBlue}
                wrapperClassName={classes.modalBodyDisclaimerLoader}
            />
        ) : query.isError ? (
            <ErrorHint isVisible />
        ) : !isEmpty(getValueFromTranslatedObject(query.data)) ? (
            <div className={classes.modalBodyDisclaimer} key={query.dataUpdatedAt}>
                <Typography
                    text={t("entities.coachingChat.guideModal.disclaimer.title")}
                    size={ETypographySize.ExtraSmall}
                    weight={ETypographyWeight.Bold}
                    className={classes.modalBodyDisclaimerTitle}
                />

                {renderRichContent({
                    identifier: rendererCssIdentifier,
                    editorClasses: classes.modalBodyDisclaimerList,
                    classes: getLegaliseClasses(rendererCssIdentifier),
                    allowedTags: legaliseAllowedTags
                })(getValueFromTranslatedObject(query.data))}

                <div ref={sentinelRef} />
            </div>
        ) : null
    }

    function renderBottomContainer(): ReactElement {
        return (
            <div className={classes.modalBodyContactContainer}>
                <ErrorHint isVisible={mutation.isError} />

                <Button
                    isCentered
                    isDisabled={Boolean(
                        isEmpty(query.data) ? false : Boolean(query.isError || mutation.isError || !wasSentinelSeen)
                    )}
                    isBusy={query.isFetching || mutation.isPending}
                    size={EButtonSize.Medium}
                    onClick={handleAction}
                    onKeyDown={handleAction}
                >
                    {query.isError ? t("common.close") : t("common.gotIt")}
                </Button>

                <CareMailBox
                    renderMessage={(): ComponentProps<typeof Typography> => ({
                        text: t("entities.coachingChat.guideModal.contactHint")
                    })}
                />
            </div>
        )
    }

    return (
        <div className={classes.modalBodyContainer}>
            {renderHeading()}

            <div className={classes.modalBodyWrapper}>
                {renderStaticList()}

                {renderDisclaimer()}

                {renderBottomContainer()}
            </div>
        </div>
    )
}

Body.displayName = bodyDisplayName

const GuardedBody: FC = withDynamicHeightGuard(Body)

/**
 * Participant Chapter CoachingMoment chat' 'Guide' modal.
 * Design: {@link https://www.figma.com/design/J6qF3Pa6zeK3udtVWLskjd/Participant?node-id=20322-39542&m=dev}
 *
 * @return {ReactElement}
 */
const Container: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    return (
        <Modal
            modalId={EModalId.Participant_CoachingChatGuide}
            modalBodyClassName={classes.modalBody}
            modalHeaderClassName={classes.modalHeader}
            title={t("entities.coachingChat.guideModal.title")}
            withDisabledBodyScroll
            withStickyHeading
        >
            <GuardedBody />
        </Modal>
    )
}

Container.displayName = "CoachingChatGuideModalContainer"

export { Container as CoachingChatGuideModalContainer }
